<template>
  <div ref="galleryContainer">
    <div
      v-if="galleryItems.length > 1"
      class="min-h-screen hidden pointer-events-none center float-right sticky z-10 lg:flex"
      style="top: 10%; --un-translate-y: -10%; transform: translateX(-1rem)"
    >
      <div class="rounded bg-grey-70 w-1 relative" style="height: 5.5rem">
        <div class="rounded bg-brand-4 w-1 transition-all ease-linear top-0 absolute" :style="{ height: `${scrollProgress}%` }" />
      </div>
    </div>
    <vf-media-gallery
      v-if="product"
      :get-item-class="getItemClasses"
      :items="_galleryItems"
      pagination
      :product-id="productId"
      @zoom="onZoom"
    >
      <template #default="{ i }">
        <base-button
          v-if="!isDigitalProduct
            && (selectedVariant?.notifyMe && $feature.showNotifyMeOnPDP && isSelectedProductOutOfStock
              ? (!favoriteCta.hideWhenNotifyMe && $feature.catalogFavorites && i === 0)
              : $feature.catalogFavorites && i === 0)
          "
          :aria-label="isFavorite ? $t.removeFromFavorites : $t.favorites"
          class="pb-4 pr-2 flex bottom-0 right-0 absolute z-1 <lg:pr-4 focus:op-100"
          :data-test-id="`pdp-${isFavorite ? 'remove' : 'add'}-favorites-cta`"
          :disabled="favorites.error"
          :loading="isFavoritePending"
          :size="ctaSize"
          :variant="isFavorite ? favoriteCta.variantSelected : ctaVariant"
          @click="toggleFavorites(productColor?.label)"
        >
          <vf-icon
            :class="{ 'c-brand-4': isFavorite }"
            :name="isFavorite ? 'heart-filled' : 'heart'"
            :size="favoriteCta.iconSize"
          />
        </base-button>
        <p
          v-if="product.modelMeasurements?.length && i === (galleryItems.length > 2 ? 2 : 0)"
          class="body-4 p-1 bg-grey-80 w-fit pointer-events-none bottom-2 left-2 absolute"
        >
          {{ product.modelMeasurements[0] }}
        </p>
      </template>
    </vf-media-gallery>
  </div>
</template>

<script setup lang="ts">
const {
  galleryItems,
  isDigitalProduct,
  isFavorite,
  isFavoritePending,
  isSelectedProductOutOfStock,
  onZoom,
  product,
  productId,
  selectedVariant,
  toggleFavorites
} = usePdp()
const { details: { ctaSize, ctaVariant, favoriteCta } } = useAppConfig().pages.pdp
const favorites = useFavoritesStore()
const productColor = useProductColor(product)
const galleryContainer = ref<HTMLElement>()
const { sketchfabProductList } = useFeatureFlags()
const { y: windowScrollY } = useScroll(window, {
  throttle: 16,
  behavior: 'smooth'
})

const INITIAL_PROGRESS = 10
const scrollProgress = ref(INITIAL_PROGRESS)
const { top, height } = useElementBounding(galleryContainer)

const updateScrollProgress = () => {
  if (!galleryContainer.value || height.value <= 0) {
    return scrollProgress.value = INITIAL_PROGRESS
  }
  const scrollableRange = height.value - window.innerHeight
  if (scrollableRange <= 0) return scrollProgress.value = INITIAL_PROGRESS
  const containerTop = top.value + windowScrollY.value
  const scrollPosition = Math.max(0, windowScrollY.value - containerTop)
  const rawProgress = (scrollPosition / scrollableRange) * 100
  const progress = INITIAL_PROGRESS + (rawProgress * (100 - INITIAL_PROGRESS)) / 100
  scrollProgress.value = Math.min(Math.max(progress, INITIAL_PROGRESS), 100)
}

watch(windowScrollY, updateScrollProgress)

const hasSketchfab = computed(() => !!sketchfabProductList[productId.value])

const _galleryItems = computed(() => {
  const baseItems = [...(galleryItems.value || [])]

  if (hasSketchfab.value) {
    const insertIndex = Math.min(3, baseItems.length)
    baseItems.splice(insertIndex, 0, {
      type: 'sketchfab',
      productId: productId.value
    })
  }

  return baseItems
})

const getItemClasses = (index: number) => {
  const count = galleryItems.value.length
  // Special cases: 1-2 images always span full width
  if (count <= 2) return 'col-span-2'
  // Define patterns for specific counts
  const patterns = {
    3: [1, 1, 2],
    4: [1, 1, 2, 2],
    5: [1, 1, 2, 1, 1],
    6: [1, 1, 2, 2, 1, 1],
    7: [1, 1, 2, 2, 1, 1, 2],
    8: [1, 1, 2, 2, 1, 1, 2, 2],
    9: [1, 1, 2, 2, 1, 1, 2, 2, 2],
    10: [1, 1, 2, 2, 1, 1, 2, 1, 1, 2],
    11: [1, 1, 2, 2, 1, 1, 2, 1, 1, 2, 2]
  }
  // Use specific pattern if available, otherwise use the base repeating pattern
  if (patterns[count]) {
    return `col-span-${patterns[count][index]}`
  }
  // For 12+ images, use the 11-image pattern and repeat
  const basePattern = patterns[11]
  const patternIndex = index % basePattern.length
  return `col-span-${basePattern[patternIndex]}`
}
</script>
